import { useCallback, useEffect, useRef, useState } from 'react';

import { getDeploymentsByRunIds, getDeploymentStatus } from '@/shared/api/deploy';
import {
  DEPLOYMENT_STATUS,
  type DeploymentDetailResponse,
  type DeploymentErrorSummary,
  type DeploymentStatus,
} from '@/shared/api/deploy/types';
import type { RunStatus } from '@/shared/ui/RunStatusTag';

import {
  calculateDeploymentStateUpdate,
  mapDeploymentStatusToRunStatus,
  shouldBootstrapDeploymentFromRunApi,
} from '@/features/deployment/lib/deploymentRunStatusMapping';

import { useDeploymentEvents } from './useDeploymentEvents';

type UseDeploymentManagerOptions = {
  runId: string;
  initialRunStatus?: RunStatus;
  onModalAutoOpen?: () => void;
};

const getFailureLogs = (summary: DeploymentErrorSummary | null, message: string, timestamp: string): string[] => {
  const newLogs = [`[${timestamp}] ❌ ${message}`];
  if (summary) {
    newLogs.push(`[${timestamp}] 오류 코드: ${summary.error_code} (${summary.failed_stage})`);
    newLogs.push(`[${timestamp}] ${summary.message}`);
    if (summary.user_action) {
      newLogs.push(`[${timestamp}] 💡 ${summary.user_action}`);
    }
  }
  return newLogs;
};

type DeploymentState = {
  deploymentId: string | null;
  status: DeploymentStatus | null;
  step: number;
  logs: string[];
  isInProgress: boolean;
  isInitialLoading: boolean;
  isLoadingDetail: boolean;
  runStatus: RunStatus;
  detail: DeploymentDetailResponse | null;
};

type DeploymentActions = {
  setDeploymentId: (id: string) => void;
  setStep: (step: number) => void;
  addLog: (message: string) => void;
  setInProgress: (value: boolean) => void;
  updateRunStatus: (status: RunStatus) => void;
  reset: () => void;
  applyServingStopResult: (detail: DeploymentDetailResponse) => void;
};

export const useDeploymentManager = ({
  runId,
  initialRunStatus = 'execution_ready',
  onModalAutoOpen,
}: UseDeploymentManagerOptions) => {
  const [deploymentId, setDeploymentIdState] = useState<string | null>(null);
  const [deploymentLogs, setDeploymentLogs] = useState<string[]>([]);
  const [currentDeploymentStatus, setCurrentDeploymentStatus] = useState<DeploymentStatus | null>(null);
  const [currentDeploymentStep, setCurrentDeploymentStep] = useState<number>(-1);
  const [isDeploymentInProgress, setIsDeploymentInProgress] = useState(false);
  const [runStatus, setRunStatus] = useState<RunStatus>(initialRunStatus);
  const [isInitialLoadingDeployment, setIsInitialLoadingDeployment] = useState(() =>
    shouldBootstrapDeploymentFromRunApi(initialRunStatus)
  );
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [deploymentDetail, setDeploymentDetail] = useState<DeploymentDetailResponse | null>(null);

  const hasSeenFailureRef = useRef(false);
  const previousStatusRef = useRef<DeploymentStatus | null>(null);
  const prevRunIdRef = useRef<string | null>(null);
  const prevInitialRunStatusRef = useRef<RunStatus | undefined>(undefined);

  useEffect(() => {
    const runIdChanged = prevRunIdRef.current !== runId;
    if (runIdChanged) {
      prevRunIdRef.current = runId;
      setDeploymentIdState(null);
      setDeploymentLogs([]);
      setCurrentDeploymentStatus(null);
      setCurrentDeploymentStep(-1);
      setIsDeploymentInProgress(false);
      setDeploymentDetail(null);
      setIsLoadingDetail(false);
      hasSeenFailureRef.current = false;
      previousStatusRef.current = null;
    }

    const statusChanged = prevInitialRunStatusRef.current !== initialRunStatus;
    if (runIdChanged || statusChanged) {
      prevInitialRunStatusRef.current = initialRunStatus;
      setRunStatus(initialRunStatus);
      setIsInitialLoadingDeployment(shouldBootstrapDeploymentFromRunApi(initialRunStatus));
    }
  }, [runId, initialRunStatus]);

  useEffect(() => {
    const fetchDeploymentStatus = async () => {
      if (!deploymentId && shouldBootstrapDeploymentFromRunApi(initialRunStatus) && runId) {
        setIsInitialLoadingDeployment(true);
        try {
          const response = await getDeploymentsByRunIds({
            run_ids: [runId],
            environment: 'dev',
            include_deleted: false,
          });

          if (response.results.length > 0 && response.results[0].found && response.results[0].deployment) {
            const foundDeployment = response.results[0].deployment;
            const foundDeploymentId = foundDeployment.deployment_id;
            const deploymentStatus = foundDeployment.status;
            const timestamp = new Date().toTimeString().slice(0, 8);

            const stateUpdate = calculateDeploymentStateUpdate(deploymentStatus, timestamp, true);

            setDeploymentIdState(foundDeploymentId);
            setRunStatus(stateUpdate.runStatus);
            setCurrentDeploymentStatus(deploymentStatus);
            setIsDeploymentInProgress(stateUpdate.isInProgress);
            setCurrentDeploymentStep(stateUpdate.step);
          }
        } catch (error) {
          console.error('[useDeploymentManager] Failed to fetch deployment by run_id:', error);
        } finally {
          setIsInitialLoadingDeployment(false);
        }
        return;
      }

      if (!deploymentId) {
        setIsInitialLoadingDeployment(false);
        return;
      }

      setIsInitialLoadingDeployment(true);
      try {
        const deploymentStatus = await getDeploymentStatus(deploymentId);

        const status = deploymentStatus.status;
        const timestamp = new Date().toTimeString().slice(0, 8);

        const stateUpdate = calculateDeploymentStateUpdate(status, timestamp, true);

        setRunStatus(stateUpdate.runStatus);
        setCurrentDeploymentStatus(status);
        setIsDeploymentInProgress(stateUpdate.isInProgress);
        setCurrentDeploymentStep(stateUpdate.step);
      } catch (error) {
        console.error('[useDeploymentManager] Failed to fetch deployment status:', error);
      } finally {
        setIsInitialLoadingDeployment(false);
      }
    };

    fetchDeploymentStatus();
  }, [deploymentId, initialRunStatus, runId]);

  const sseEnabled = !isInitialLoadingDeployment && isDeploymentInProgress;

  useDeploymentEvents(deploymentId, {
    enabled: sseEnabled,
    onStatusChange: (status, change) => {
      const previousStatus = previousStatusRef.current;
      const isStatusChanged = previousStatus !== status;
      const timestamp = new Date().toTimeString().slice(0, 8);

      if (hasSeenFailureRef.current && status === DEPLOYMENT_STATUS.SERVING_INACTIVE) {
        return;
      }

      previousStatusRef.current = status;

      const hasError = !!change.last_error_summary;

      if (status === DEPLOYMENT_STATUS.SERVING_INACTIVE && hasError) {
        setCurrentDeploymentStatus(DEPLOYMENT_STATUS.DEPLOYMENT_FAILED);
      } else {
        setCurrentDeploymentStatus(status);
      }

      if (isStatusChanged) {
        switch (status) {
          case DEPLOYMENT_STATUS.SERVING_INACTIVE:
            if (!hasError) {
              setDeploymentLogs((prev) => [...prev, `[${timestamp}] 배포 대기 중입니다`]);
              setCurrentDeploymentStep(0);
            }
            break;

          case DEPLOYMENT_STATUS.DEPLOYMENT_IN_PROGRESS:
            setDeploymentLogs((prev) => [...prev, `[${timestamp}] 배포를 진행하고 있습니다`]);
            setCurrentDeploymentStep(0);
            break;

          case DEPLOYMENT_STATUS.DEPLOYMENT_SUCCEEDED:
            setDeploymentLogs((prev) => [...prev, `[${timestamp}] 패키징이 완료되었습니다. 서빙을 시작합니다`]);
            setCurrentDeploymentStep(1);
            break;

          case DEPLOYMENT_STATUS.SERVING_IN_PROGRESS:
            setDeploymentLogs((prev) => [...prev, `[${timestamp}] 서빙을 준비하고 있습니다`]);
            setCurrentDeploymentStep(2);
            break;

          case DEPLOYMENT_STATUS.SERVING_ACTIVE:
            setDeploymentLogs((prev) => [...prev, `[${timestamp}] ✅ 서빙이 활성화되었습니다`]);
            setCurrentDeploymentStep(3);
            break;

          case DEPLOYMENT_STATUS.DEPLOYMENT_FAILED:
            setDeploymentLogs((prev) => [
              ...prev,
              ...getFailureLogs(change.last_error_summary, '배포에 실패했습니다', timestamp),
            ]);
            setCurrentDeploymentStep(0);
            break;

          case DEPLOYMENT_STATUS.SERVING_FAILED:
            setDeploymentLogs((prev) => [
              ...prev,
              ...getFailureLogs(change.last_error_summary, '서빙 활성화에 실패했습니다', timestamp),
            ]);
            setCurrentDeploymentStep(2);
            break;
        }
      }

      if (status === DEPLOYMENT_STATUS.SERVING_ACTIVE) {
        setRunStatus('serving_active');
        setIsDeploymentInProgress(false);
        hasSeenFailureRef.current = false;

        if (deploymentId) {
          setIsLoadingDetail(true);
          getDeploymentStatus(deploymentId)
            .then((detail) => {
              setDeploymentDetail(detail);
            })
            .catch((error) => {
              console.error('[useDeploymentManager] Failed to fetch deployment detail:', error);
            })
            .finally(() => {
              setIsLoadingDetail(false);
            });
        }

        if (onModalAutoOpen) {
          onModalAutoOpen();
        }
      } else if (status === DEPLOYMENT_STATUS.DEPLOYMENT_IN_PROGRESS) {
        setRunStatus('deployment_in_progress');
        setIsDeploymentInProgress(true);
      } else if (status === DEPLOYMENT_STATUS.DEPLOYMENT_SUCCEEDED) {
        setRunStatus('serving_inactive');
        setIsDeploymentInProgress(true);
      } else if (status === DEPLOYMENT_STATUS.SERVING_IN_PROGRESS) {
        setRunStatus('deployment_in_progress');
        setIsDeploymentInProgress(true);
      } else if (status === DEPLOYMENT_STATUS.SERVING_INACTIVE) {
        if (hasError) {
          if (isStatusChanged) {
            const failureTimestamp = new Date().toTimeString().slice(0, 8);
            setDeploymentLogs((prev) => [
              ...prev,
              ...getFailureLogs(change.last_error_summary, '서빙 활성화에 실패했습니다', failureTimestamp),
            ]);
            setCurrentDeploymentStep(2);
          }

          setRunStatus('deployment_failed');
          setIsDeploymentInProgress(false);
          hasSeenFailureRef.current = true;

          if (onModalAutoOpen) {
            onModalAutoOpen();
          }
        } else {
          setRunStatus('serving_inactive');
        }
      } else if (status === DEPLOYMENT_STATUS.DEPLOYMENT_FAILED || status === DEPLOYMENT_STATUS.SERVING_FAILED) {
        setRunStatus('deployment_failed');
        setIsDeploymentInProgress(false);
        hasSeenFailureRef.current = true;

        if (onModalAutoOpen) {
          onModalAutoOpen();
        }
      }
    },
  });

  const setDeploymentId = useCallback((id: string) => {
    setDeploymentIdState(id);
    setCurrentDeploymentStatus(DEPLOYMENT_STATUS.SERVING_INACTIVE);
    setCurrentDeploymentStep(0);
    setIsDeploymentInProgress(true);
    hasSeenFailureRef.current = false;
    previousStatusRef.current = null;
  }, []);

  const setStep = useCallback((step: number) => {
    setCurrentDeploymentStep(step);
  }, []);

  const addLog = useCallback((message: string) => {
    setDeploymentLogs((prev) => [...prev, message]);
  }, []);

  const setInProgress = useCallback((value: boolean) => {
    setIsDeploymentInProgress(value);
  }, []);

  const updateRunStatus = useCallback((status: RunStatus) => {
    setRunStatus(status);
  }, []);

  const reset = useCallback(() => {
    setDeploymentIdState(null);
    setDeploymentLogs([]);
    setCurrentDeploymentStatus(null);
    setCurrentDeploymentStep(-1);
    setIsDeploymentInProgress(false);
    hasSeenFailureRef.current = false;
    previousStatusRef.current = null;
  }, []);

  const applyServingStopResult = useCallback((detail: DeploymentDetailResponse) => {
    setDeploymentDetail(detail);
    setCurrentDeploymentStatus(detail.status);
    setRunStatus(mapDeploymentStatusToRunStatus(detail.status));
  }, []);

  const state: DeploymentState = {
    deploymentId,
    status: currentDeploymentStatus,
    step: currentDeploymentStep,
    logs: deploymentLogs,
    isInProgress: isDeploymentInProgress,
    isInitialLoading: isInitialLoadingDeployment,
    isLoadingDetail,
    runStatus,
    detail: deploymentDetail,
  };

  const actions: DeploymentActions = {
    setDeploymentId,
    setStep,
    addLog,
    setInProgress,
    updateRunStatus,
    reset,
    applyServingStopResult,
  };

  return {
    state,
    actions,
  };
};
